import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { uploadAsset } from '../../lib/assetsApi';

const { FiUpload, FiCheck, FiX, FiRefreshCw } = FiIcons;

const UploadTester = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const uploadResult = await uploadAsset(file, 'test');
      setResult(uploadResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Tester</h1>
          <p className="text-gray-600">Test file uploads to Dropbox storage</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="space-y-6">
            {/* File Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File
              </label>
              <input
                type="file"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            {/* File Info */}
            {file && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">File Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <p className="font-medium">{file.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Size:</span>
                    <p className="font-medium">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <p className="font-medium">{file.type}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon 
                  icon={uploading ? FiRefreshCw : FiUpload} 
                  className={`w-4 h-4 ${uploading ? 'animate-spin' : ''}`} 
                />
                <span>{uploading ? 'Uploading...' : 'Upload File'}</span>
              </button>

              <button
                onClick={reset}
                className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                <span>Reset</span>
              </button>
            </div>

            {/* Results */}
            {result && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-800">Upload Successful</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-green-700 font-medium">Path:</span>
                    <p className="text-green-600 font-mono">{result.path}</p>
                  </div>
                  {result.url && (
                    <div>
                      <span className="text-green-700 font-medium">URL:</span>
                      <p className="text-green-600 break-all">
                        <a 
                          href={result.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {result.url}
                        </a>
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-green-700 font-medium">Uploaded:</span>
                    <p className="text-green-600">{new Date(result.uploaded_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiX} className="w-5 h-5 text-red-600" />
                  <h3 className="font-medium text-red-800">Upload Failed</h3>
                </div>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UploadTester;